import { ArrowRight } from "lucide-react";
import { useTranslate } from "ra-core";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { deliveryHealth } from "@/components/fretum/delivery/useDelivery";

import { dayKey, toLocalDate } from "../../mobile/mobileModel";
import type { Deal } from "../../types";
import type {
  DeliveryRow,
  FixedCosts,
  FunnelStage,
  HoursSummary,
} from "./pulseModel";
import { useHomeFormat } from "./useHomeFormat";

const Card = ({
  id,
  title,
  subtitle,
  aside,
  children,
  className,
}: {
  id: string;
  title: string;
  subtitle?: string;
  aside?: ReactNode;
  children: ReactNode;
  className?: string;
}) => (
  <section className={`home-card ${className ?? ""}`} aria-labelledby={id}>
    <header className="home-card__head">
      <h2 id={id} className="home-card__title">
        {title}
        {subtitle ? (
          <span className="home-card__subtitle">{subtitle}</span>
        ) : null}
      </h2>
      {aside}
    </header>
    {children}
  </section>
);

/** «Embudo por etapa»: open deals per stage, value and count, one hue. */
export const StageFunnel = ({
  stages,
  overdue,
}: {
  stages: FunnelStage[];
  overdue: Deal[];
}) => {
  const translate = useTranslate();
  const format = useHomeFormat();
  const t = (key: string, options?: Record<string, unknown>) =>
    translate(`desk.home.pulse.funnel.${key}`, options);
  const max = Math.max(...stages.map((stage) => stage.amount), 1);
  const shown = stages.filter((stage) => stage.count > 0);
  return (
    <Card id="home-funnel-title" title={t("title")} subtitle={t("subtitle")}>
      {shown.length === 0 ? (
        <p className="home-agenda__empty">{t("empty")}</p>
      ) : (
        <ul className="home-funnel">
          {shown.map((stage) => (
            <li key={stage.stage}>
              <span className="home-funnel__label">
                {stage.label}
                <small>{t("deals", { smart_count: stage.count })}</small>
              </span>
              <span className="home-funnel__track" aria-hidden>
                <span style={{ width: `${(stage.amount / max) * 100}%` }} />
              </span>
              <span className="home-funnel__value">
                {format.money(stage.amount)}
              </span>
            </li>
          ))}
        </ul>
      )}
      {overdue.length > 0 ? (
        <div className="home-funnel__overdue">
          <h3>{t("overdue_closings", { smart_count: overdue.length })}</h3>
          <ul>
            {overdue.slice(0, 3).map((deal) => (
              <li key={deal.id}>
                <Link to={`/deals/${deal.id}/show`}>{deal.name}</Link>
                <span>
                  {format.shortDate(toLocalDate(deal.expected_closing_date))}
                </span>
                <b>{format.money(deal.amount)}</b>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </Card>
  );
};

const HEALTH_TONE = {
  on_track: "positive",
  at_risk: "warning",
  late: "negative",
} as const;

/** «Proyectos en entrega»: milestone progress, health and next milestone. */
export const DeliveryTable = ({ rows }: { rows: DeliveryRow[] }) => {
  const translate = useTranslate();
  const format = useHomeFormat();
  const t = (key: string, options?: Record<string, unknown>) =>
    translate(`desk.home.pulse.deliveries.${key}`, options);
  const today = dayKey(new Date());
  return (
    <Card
      id="home-deliveries-title"
      title={t("title")}
      subtitle={t("active", { smart_count: rows.length })}
      aside={
        <Link
          to="/deals"
          className="home-icon-link"
          aria-label={t("see_all")}
          title={t("see_all")}
        >
          <ArrowRight aria-hidden className="size-4" strokeWidth={1.75} />
        </Link>
      }
    >
      {rows.length === 0 ? (
        <p className="home-agenda__empty">{t("empty")}</p>
      ) : (
        <table className="home-table">
          <thead>
            <tr>
              <th scope="col">{t("project")}</th>
              <th scope="col">{t("progress")}</th>
              <th scope="col">{t("status")}</th>
              <th scope="col" className="text-right whitespace-nowrap">
                {t("next")}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 5).map((row) => {
              const health = deliveryHealth({
                id: row.dealId,
                progress: row.progress,
                nb_milestones: row.milestones,
                nb_milestones_done: row.milestonesDone,
                nb_overdue: row.nbOverdue,
                next_due_date: row.nextDue,
              });
              return (
                <tr key={row.dealId}>
                  <th scope="row">
                    <Link to={`/deals/${row.dealId}/show`}>
                      {row.name || "—"}
                    </Link>
                    <small>
                      {t("milestones", {
                        done: row.milestonesDone,
                        total: row.milestones,
                      })}
                      {row.nextTitle ? ` · ${row.nextTitle}` : ""}
                    </small>
                  </th>
                  <td>
                    <span className="home-progress">
                      <span aria-hidden>
                        <span style={{ width: `${row.progress}%` }} />
                      </span>
                      {row.progress}%
                    </span>
                  </td>
                  <td>
                    <span
                      className="home-badge"
                      data-tone={HEALTH_TONE[health]}
                    >
                      {translate(`hub.delivery.${health}`)}
                    </span>
                  </td>
                  <td className="text-right tabular-nums whitespace-nowrap">
                    {row.nextDue
                      ? dayKey(row.nextDue) === today
                        ? t("today")
                        : format.shortDate(toLocalDate(row.nextDue))
                      : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Card>
  );
};

/** «Horas del mes»: total, billable share and hours per deal (no capacity). */
export const HoursCard = ({
  hours,
  deals,
}: {
  hours: HoursSummary;
  deals: Deal[];
}) => {
  const translate = useTranslate();
  const format = useHomeFormat();
  const t = (key: string, options?: Record<string, unknown>) =>
    translate(`desk.home.pulse.hours.${key}`, options);
  const max = Math.max(...hours.byDeal.map((row) => row.hours), 1);
  const nameOf = (id: DeliveryRow["dealId"] | null) =>
    id == null
      ? t("no_deal")
      : (deals.find((deal) => String(deal.id) === String(id))?.name ?? "—");
  return (
    <Card
      id="home-hours-title"
      title={t("title", { month: format.monthName(new Date()) })}
      aside={
        hours.total > 0 ? (
          <span className="home-badge" data-tone="positive">
            {t("billable", { percent: format.percent(hours.billableRatio) })}
          </span>
        ) : null
      }
    >
      {hours.total === 0 ? (
        <p className="home-agenda__empty">{t("empty")}</p>
      ) : (
        <>
          <p className="home-big">
            {format.number(hours.total)} h<small>{t("logged")}</small>
          </p>
          <ul className="home-funnel home-funnel--compact">
            {hours.byDeal.slice(0, 4).map((row) => (
              <li key={String(row.dealId)}>
                <span className="home-funnel__label">{nameOf(row.dealId)}</span>
                <span className="home-funnel__track" aria-hidden>
                  <span
                    data-muted={row.dealId == null || undefined}
                    style={{ width: `${(row.hours / max) * 100}%` }}
                  />
                </span>
                <span className="home-funnel__value">
                  {format.number(row.hours)} h
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </Card>
  );
};

/** «Costes fijos»: active subscriptions per month and the next renewals. */
export const FixedCostsCard = ({ costs }: { costs: FixedCosts }) => {
  const translate = useTranslate();
  const format = useHomeFormat();
  const t = (key: string, options?: Record<string, unknown>) =>
    translate(`desk.home.pulse.costs.${key}`, options);
  return (
    <Card
      id="home-costs-title"
      title={t("title")}
      subtitle={t("subtitle")}
      aside={
        <span className="home-badge" data-tone="neutral">
          {t("active", { smart_count: costs.count })}
        </span>
      }
    >
      {costs.count === 0 ? (
        <p className="home-agenda__empty">{t("empty")}</p>
      ) : (
        <>
          <p className="home-big">
            {format.money(costs.monthly)}
            <small>
              {t("per_month", { yearly: format.money(costs.yearly) })}
            </small>
          </p>
          {costs.upcoming.length > 0 ? (
            <>
              <h3 className="home-mini-title">{t("renewals")}</h3>
              <ul className="home-renewals">
                {costs.upcoming.map((item) => (
                  <li key={item.id}>
                    <span className="home-renewals__avatar" aria-hidden>
                      {item.name.slice(0, 2).toUpperCase()}
                    </span>
                    <span className="min-w-0">
                      <b>{item.name}</b>
                      <small>
                        {translate(
                          `desk.home.pulse.costs.periods.${item.billing_period}`,
                        )}
                      </small>
                    </span>
                    <span className="home-renewals__amount">
                      <b>{format.money(item.amount)}</b>
                      <small>
                        {item.next_renewal
                          ? format.shortDate(toLocalDate(item.next_renewal))
                          : ""}
                      </small>
                    </span>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
          <Link to="/subscriptions" className="home-link">
            {t("see_all")}
          </Link>
        </>
      )}
    </Card>
  );
};
